import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Rating,
  Container,
  Paper,
  Grid,
  Alert,
  Snackbar,
  FormControlLabel,
  Checkbox,
  useTheme,
  keyframes
} from '@mui/material';
import { Send, Star } from '@mui/icons-material';

// Keyframe animations
const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const FeedbackPage: React.FC = () => {
  const theme = useTheme();
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [rating, setRating] = useState<number | null>(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [consentPublish, setConsentPublish] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    // Validation
    if (!name || !feedbackText || !rating) {
      setErrorMessage('Bitte füllen Sie Name, Feedback und Bewertung aus.');
      setShowError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          company: company || null,
          email: email || null,
          rating,
          feedback_text: feedbackText,
          consent_publish: consentPublish,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Fehler beim Absenden');
      }

      // Success
      setShowSuccess(true);

      // Reset form
      setName('');
      setCompany('');
      setEmail('');
      setRating(0);
      setFeedbackText('');
      setConsentPublish(false);
    } catch (error: any) {
      setErrorMessage(error.message || 'Ein Fehler ist aufgetreten');
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '80vh',
        py: 8,
        background: `radial-gradient(circle at 20% 20%, ${theme.palette.primary.main}08 0%, transparent 50%),
                     radial-gradient(circle at 80% 80%, ${theme.palette.secondary.main}06 0%, transparent 50%)`,
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            textAlign: 'center',
            mb: 6,
            animation: `${slideInUp} 0.8s ease-out`,
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Ihr Feedback ist wichtig
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 700, mx: 'auto', lineHeight: 1.7 }}
          >
            Helfen Sie uns, Smart Storage Checker zu verbessern. Teilen Sie Ihre Erfahrung
            und tragen Sie zur Entwicklung einer besseren Lösung bei.
          </Typography>
        </Box>

        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: '24px',
            background: `linear-gradient(135deg, ${theme.palette.background.paper}95 0%, ${theme.palette.background.paper}85 40%, ${theme.palette.primary.main}08 75%, ${theme.palette.primary.main}05 100%)`,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${theme.palette.primary.main}20`,
            boxShadow: `0 20px 60px ${theme.palette.primary.main}15`,
            animation: `${slideInUp} 1s ease-out 0.2s both`,
          }}
        >
          <Grid container spacing={3}>
            {/* Name Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: 'background.paper',
                  },
                }}
              />
            </Grid>

            {/* Company Field */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Unternehmen (optional)"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: 'background.paper',
                  },
                }}
              />
            </Grid>

            {/* Email Field */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="email"
                label="E-Mail (optional)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="outlined"
                helperText="Falls Sie eine Antwort wünschen"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: 'background.paper',
                  },
                }}
              />
            </Grid>

            {/* Rating */}
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  sx={{ mb: 2, fontWeight: 500 }}
                >
                  Wie zufrieden sind Sie mit Smart Storage Checker? *
                </Typography>
                <Rating
                  value={rating}
                  onChange={(event, newValue) => setRating(newValue)}
                  size="large"
                  icon={<Star fontSize="inherit" />}
                  sx={{
                    '& .MuiRating-iconFilled': {
                      color: theme.palette.primary.main,
                    },
                    '& .MuiRating-iconHover': {
                      color: theme.palette.primary.dark,
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* Feedback Text */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={6}
                label="Ihr Feedback"
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                variant="outlined"
                placeholder="Was gefällt Ihnen? Was können wir verbessern? Teilen Sie uns Ihre Gedanken mit..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: 'background.paper',
                  },
                }}
              />
            </Grid>

            {/* Consent Checkbox */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentPublish}
                    onChange={(e) => setConsentPublish(e.target.checked)}
                    sx={{
                      color: theme.palette.primary.main,
                      '&.Mui-checked': {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Ich bin damit einverstanden, dass mein Feedback als Testimonial
                    veröffentlicht werden kann (nur mit Name und Unternehmen, keine E-Mail)
                  </Typography>
                }
              />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12} sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={isSubmitting}
                startIcon={<Send />}
                sx={{
                  padding: '16px 48px',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: '12px',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  boxShadow: `0 8px 32px ${theme.palette.primary.main}40`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: `0 12px 48px ${theme.palette.primary.main}60`,
                  },
                  '&:disabled': {
                    opacity: 0.6,
                  },
                }}
              >
                {isSubmitting ? 'Wird gesendet...' : 'Feedback absenden'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* Success/Error Snackbars */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{ borderRadius: '12px', fontSize: '1rem' }}
        >
          Vielen Dank für Ihr wertvolles Feedback! Wir schätzen Ihre Meinung sehr.
        </Alert>
      </Snackbar>
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity="error"
          sx={{ borderRadius: '12px', fontSize: '1rem' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeedbackPage;
